import { DEFAULT_CONVERSION_CURRENCY } from 'components/features/submission-details/types';
import type React from 'react';
import { useCallback, useRef, useState } from 'react';
import { SubmissionTabProps } from 'routes/Submissions/SubmissionDetail/navigation-tab/submission-config-tabs';
import SubmissionCurrencyTabProps from 'components/features/submission-currency-tabs';
import FormActionButtons from 'components/ui/form-action-buttons';
import { useTranslation } from 'react-i18next';
import CatPricingSummaryTable from 'components/features/cat-rating/cat-pricing-summary-table';
import CatPricingAnalysisTable from 'components/features/cat-rating/cat-pricing-analysis-table';
import FormSection from 'components/ui/form-section';
import { EFormSectionStatus } from 'components/ui/form-section/types';
import {
  BasisOfCoverResponseDtoBasisOfCoverEnum,
  BasisOfCoverResponseDtoCategoryEnum,
  CatPricingSummaryResponseDto,
  GetTailoredResultQueryDtoPerilEnum,
  GetTailoredResultQueryDtoPriceRatingTypeEnum,
} from 'libs/pinnacle-sdk';
import useSocketIO, { ESocketEvents } from 'hooks/useWebSocket';
import {
  basisOfCoverApi,
  priceRatingApi,
  priceRatingGatewayUrl,
} from 'services/apis';
import { useQuery } from 'react-query';
import { EQueryKeys } from 'constants/query-keys';
import { IPriceRatingUpdatedMessage } from 'services/apis/types';

const CatRating: React.FC<SubmissionTabProps> = ({
  submission,
  includeBack,
  includeNext,
  navigateBack,
  navigateNext,
}) => {
  const [activeTab, setActiveTab] = useState<string>(
    submission.originalCurrency || DEFAULT_CONVERSION_CURRENCY
  );
  const { t } = useTranslation();
  const [hasErrors, setHasErrors] = useState(false);
  const eqRef = useRef<{
    refetch: () => Promise<unknown>;
  }>(null);
  const flRef = useRef<{
    refetch: () => Promise<unknown>;
  }>(null);
  const tcRef = useRef<{
    refetch: () => Promise<unknown>;
  }>(null);

  const {
    data: catPricingSummary,
    isLoading: isCatPricingSummaryLoading,
    refetch: refetchCatPricingSummary,
  } = useQuery<CatPricingSummaryResponseDto[]>(
    [EQueryKeys.CAT_PRICING_SUMMARY, submission.submissionId, activeTab],
    () =>
      priceRatingApi.getCatPricingSummary(submission.submissionId, activeTab),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { data: basisOfCovers } = useQuery(
    [EQueryKeys.BASIS_OF_COVERS, submission.submissionId],
    () =>
      basisOfCoverApi.getBasisOfCovers({
        submissionId: submission.submissionId,
      }),
    {
      refetchOnWindowFocus: false,
    }
  );
  const handleErrorChange = (hasError: boolean) => {
    setHasErrors(hasError);
  };

  const refetchCatRating = useCallback(
    async () =>
      Promise.all([
        refetchCatPricingSummary(),
        eqRef.current?.refetch(),
        flRef.current?.refetch(),
        tcRef.current?.refetch(),
      ]),
    [refetchCatPricingSummary, eqRef, flRef, tcRef]
  );

  const { sendMessage } = useSocketIO<IPriceRatingUpdatedMessage>({
    url: priceRatingGatewayUrl,
    onConnect: () => {
      sendMessage(ESocketEvents.SUBSCRIBE, {
        submissionId: submission.submissionId,
      });
    },
    registerEvents: [
      {
        event: ESocketEvents.PRICE_RATING_UPDATED,
        onMessage: async (message) => {
          const {
            data: { type },
            submissionId,
          } = message;
          if (
            type === GetTailoredResultQueryDtoPriceRatingTypeEnum.CatRating &&
            submissionId === submission.submissionId
          ) {
            await refetchCatRating();
          }
        },
      },
    ],
  });

  return (
    <div className="rounded-lg bg-[#E3F2FB]">
      <div className="flex items-center justify-between">
        <SubmissionCurrencyTabProps
          originalCurrency={submission.originalCurrency}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>
      <div className="flex flex-col gap-5 mb-4">
        <CatPricingSummaryTable
          catPricingSummary={catPricingSummary || []}
          isCatPricingSummaryLoading={isCatPricingSummaryLoading}
        />
        <FormSection
          title="Earthquake"
          collapsible
          status={EFormSectionStatus.FILLING}
        >
          <CatPricingAnalysisTable
            onErrorChange={handleErrorChange}
            submissionStatus={submission.status}
            basisOfCover={
              (basisOfCovers || []).find(
                (basisOfCover) =>
                  basisOfCover.category ===
                  BasisOfCoverResponseDtoCategoryEnum.Earthquake
              )?.basisOfCover ||
              BasisOfCoverResponseDtoBasisOfCoverEnum.AnyOneEvent
            }
            submissionId={submission.submissionId}
            peril={GetTailoredResultQueryDtoPerilEnum.Eq}
            ref={eqRef}
            sendMessage={sendMessage}
            currency={activeTab}
            originalCurrency={submission.originalCurrency}
          />
        </FormSection>
        <FormSection
          title="Flood"
          collapsible
          status={EFormSectionStatus.FILLING}
        >
          <CatPricingAnalysisTable
            onErrorChange={handleErrorChange}
            submissionStatus={submission.status}
            basisOfCover={
              (basisOfCovers || []).find(
                (basisOfCover) =>
                  basisOfCover.category ===
                  BasisOfCoverResponseDtoCategoryEnum.Flood
              )?.basisOfCover ||
              BasisOfCoverResponseDtoBasisOfCoverEnum.AnyOneEvent
            }
            submissionId={submission.submissionId}
            peril={GetTailoredResultQueryDtoPerilEnum.Fl}
            ref={flRef}
            currency={activeTab}
            originalCurrency={submission.originalCurrency}
            sendMessage={sendMessage}
          />
        </FormSection>
        <FormSection
          title="Wind"
          collapsible
          status={EFormSectionStatus.FILLING}
        >
          <CatPricingAnalysisTable
            onErrorChange={handleErrorChange}
            submissionStatus={submission.status}
            basisOfCover={
              (basisOfCovers || []).find(
                (basisOfCover) =>
                  basisOfCover.category ===
                  BasisOfCoverResponseDtoCategoryEnum.Wind
              )?.basisOfCover ||
              BasisOfCoverResponseDtoBasisOfCoverEnum.AnyOneEvent
            }
            submissionId={submission.submissionId}
            peril={GetTailoredResultQueryDtoPerilEnum.Tc}
            ref={tcRef}
            currency={activeTab}
            originalCurrency={submission.originalCurrency}
            sendMessage={sendMessage}
          />
        </FormSection>
      </div>

      <FormActionButtons
        confirmText={t('general.next')}
        cancelText={t('general.back')}
        isConfirmDisabled={hasErrors}
        includeCancel={includeBack}
        includeConfirm={includeNext}
        confirmOutlined={false}
        onCancel={navigateBack}
        onConfirm={navigateNext}
      />
    </div>
  );
};

export default CatRating;
